import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Heart, ImageOff, X, ArrowRight, AlertTriangle,
  Droplets, Cookie, Droplet, Flame, Fish, Wheat, FlaskConical, Leaf, Info, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { hasBarcodeFormat, hasValidCheckDigit } from "@/lib/barcode";
import { Button } from "@/components/ui/button";
import { getLocalPrefs, evaluateProduct } from "@/lib/prefs";
import { recordScanDay } from "@/lib/streak";
import { ShareProduct } from "@/components/ShareProduct";
import { useAuth } from "@/context/AuthContext";
import { GridCard } from "@/components/cards";
import { gradeFromScore, STATUS_COLOR } from "@/lib/rating";
import { useI18n } from "@/i18n/I18nContext";
import { api, resolveImage } from "@/lib/api";

const ICONS = { salt: Droplets, sugar: Cookie, saturated_fat: Droplet, energy: Flame,
  protein: Fish, fiber: Wheat, carbs: Cookie, additives: FlaskConical, palm: Leaf, trans: Droplet };

const fmtNum = (n) => (n == null ? "" : String(n).replace(/\.0+$/, ""));

const WHO_NORMS = { sugar: "who.sugar", salt: "who.salt", saturated_fat: "who.saturated_fat" };

const SAFETY = {
  safe: { color: "#16A34A", bg: "#ECFDF5", key: "add.safe" },
  controversial: { color: "#F97316", bg: "#FFF7ED", key: "add.controversial" },
  dangerous: { color: "#DC2626", bg: "#FEF2F2", key: "add.dangerous" },
  unknown: { color: "#94A3B8", bg: "#F8FAFC", key: "add.unknown" },
};

const AdditiveCard = ({ a }) => {
  const { t } = useI18n();
  const s = SAFETY[a.safety] || SAFETY.unknown;
  const url = `https://scholar.google.com/scholar?q=${encodeURIComponent(`${a.code} ${a.name} food additive safety`)}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`additive-${a.code}`}
      className="block rounded-xl border p-3 active:scale-[0.99] transition-transform"
      style={{ background: s.bg, borderColor: `${s.color}33` }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-bold text-slate-800 text-sm">{a.code}</span>
        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-white" style={{ background: s.color }}>{t(s.key)}</span>
        <span className="ml-auto flex items-center gap-1 text-xs font-semibold" style={{ color: s.color }}>
          <FileText className="w-3.5 h-3.5" /> {t("add.readStudies")} <ExternalLink className="w-3 h-3" />
        </span>
      </div>
      <p className="text-sm font-medium text-slate-700">{a.name}</p>
      {a.note && <p className="text-xs text-slate-500 mt-0.5 leading-snug">{a.note}</p>}
    </a>
  );
};

const NutrientRow = ({ fact, additives }) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const Icon = ICONS[fact.key] || FlaskConical;
  const color = STATUS_COLOR[fact.status];
  const label = t(`nutrient.${fact.key}`);
  const sublabel = fact.subkey ? t(`factsub.${fact.subkey}`) : fact.sublabel;
  let value = fact.value;
  if (fact.unit) value = `${fmtNum(fact.raw)} ${t(`unit.${fact.unit}`)}`;
  else if (typeof fact.bool === "boolean") value = t(fact.bool ? "common.yes" : "common.no");

  const isAdditives = fact.key === "additives" && additives && additives.length > 0;
  const whoKey = WHO_NORMS[fact.key];
  const expandable = isAdditives || !!whoKey;

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`fact-${fact.key}`}>
      <button onClick={() => expandable && setOpen(!open)} disabled={!expandable}
        data-testid={expandable ? `fact-expand-${fact.key}` : undefined}
        className={`w-full flex items-center gap-3 py-3.5 text-left ${expandable ? "" : "cursor-default"}`}>
        <Icon className="w-6 h-6 text-slate-400 shrink-0" strokeWidth={1.8} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800">{label}</p>
          <p className="text-sm text-slate-400">{sublabel}</p>
        </div>
        <span className="text-sm font-medium text-slate-600">{value}</span>
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
        {expandable ? (open ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />) : <span className="w-4" />}
      </button>
      {open && expandable && (
        <div className="pb-4 pl-9 pr-1 space-y-2" data-testid={`fact-detail-${fact.key}`}>
          {whoKey && (
            <div className="rounded-xl bg-[#FBF8F2] border border-black/5 p-3 flex gap-2">
              <Info className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-semibold text-slate-700 mb-0.5">{t("who.title")}</p>
                {t(whoKey)}
              </div>
            </div>
          )}
          {isAdditives && (
            <>
              <p className="text-xs text-slate-400 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> {t("add.tapHint")}</p>
              {additives.map((a) => <AdditiveCard key={a.code} a={a} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default function ProductDetail() {
  const { barcode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [product, setProduct] = useState(null);
  const [alts, setAlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imgErr, setImgErr] = useState(false);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    setLoading(true); setError(""); setImgErr(false);
    if (!hasBarcodeFormat(barcode)) { setError("__invalid__"); setLoading(false); return; }
    api.get(`/products/barcode/${barcode}`)
      .then(async (r) => {
        setProduct(r.data);
        recordScanDay();
        const alt = await api.get(`/products/barcode/${barcode}/alternatives`).catch(() => ({ data: [] }));
        setAlts(alt.data.map((p) => ({ ...p, score_total: p.score.total })));
      })
      .catch((e) => {
        const st = e?.response?.status;
        if (st === 404 || !e?.response) setError(hasValidCheckDigit(barcode) ? "__notfound__" : "__invalid__");
        else setError(e.response.data?.detail || "__notfound__");
      })
      .finally(() => setLoading(false));
  }, [barcode]);

  useEffect(() => {
    if (!user || !product) return;
    api.post("/scans/record", { barcode: product.barcode }).catch(() => {});
    api.get("/favorites").then((r) => setIsFav(r.data.some((f) => f.barcode === product.barcode))).catch(() => {});
  }, [user, product?.barcode]);

  const toggleFav = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      if (isFav) { await api.delete(`/favorites/${product.barcode}`); setIsFav(false); toast(t("favorites.removed")); }
      else { await api.post("/favorites", { barcode: product.barcode }); setIsFav(true); toast.success(t("product.favAdded")); }
    } catch { toast.error(t("product.error")); }
  };

  const g = product ? gradeFromScore(product.score.total) : null;
  const img = product ? resolveImage(product.image_url, 400) : "";
  const facts = product?.score?.facts || { negatives: [], positives: [] };
  const prefs = getLocalPrefs();
  const activePrefs = user?.preferences && Object.keys(user.preferences).length ? { ...prefs, ...user.preferences } : prefs;
  const evalRes = product ? evaluateProduct(product.score, activePrefs, t) : { conflicts: [], matches: [] };
  const certs = product?.score?.certificates || [];
  const nova = product?.score?.nova;
  const additivesItems = product?.score?.breakdown?.additives?.items || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center" data-testid="product-sheet">
      <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="w-full max-w-[430px] bg-[#F4F0EA] rounded-t-3xl max-h-[94vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#F4F0EA] z-10 pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1.5 rounded-full bg-slate-200" />
          <button onClick={() => navigate(-1)} data-testid="sheet-close" className="absolute right-4 top-2 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>
        ) : error ? (
          <div className="p-10 text-center" data-testid="product-error">
            <p className="font-heading font-bold text-lg text-slate-800" data-testid="product-error-message">
              {error === "__invalid__" ? t("scan.invalidBarcode") : error === "__notfound__" ? t("product.notInBase") : error}
            </p>
            <p className="text-slate-400 mt-1">{t("product.barcode")}: {barcode}</p>
            <Button onClick={() => navigate("/scan")} data-testid="scan-again-button" className="mt-5 rounded-full bg-primary">
              {t("product.scanAgain")}
            </Button>
          </div>
        ) : product && (
          <div className="px-5 pb-10">
            {/* header */}
            <div className="flex gap-4 pt-2">
              <div className="w-24 h-24 rounded-2xl bg-[#FBF8F2] overflow-hidden flex items-center justify-center shrink-0 border border-black/5">
                {img && !imgErr ? <img src={img} alt="" loading="eager" decoding="async" onError={() => setImgErr(true)} className="w-full h-full object-contain p-1" data-testid="product-image" />
                  : <ImageOff className="w-8 h-8 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-xl font-extrabold text-slate-900 leading-tight" data-testid="product-name">{product.name}</h1>
                <p className="text-sm text-slate-400 mb-2">{product.brand || "—"}</p>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: g.solid }} />
                  <span className="font-heading text-2xl font-extrabold" style={{ color: g.text }} data-testid="score-value">{product.score.total}<span className="text-base text-slate-300">/100</span></span>
                  <span className="text-sm font-semibold" style={{ color: g.text }} data-testid="score-grade">{t(`grade.${g.key}`)}</span>
                </div>
                {product.score.data?.applied && (
                  <div data-testid="data-warning" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ background: product.score.data.level === "low" ? "#FEE2E2" : "#FEF3C7", color: product.score.data.level === "low" ? "#B91C1C" : "#B45309" }}>
                    <AlertTriangle className="w-3.5 h-3.5" /> {product.score.data.level === "low" ? t("product.dataLow") : t("product.dataPartial")}
                  </div>
                )}
              </div>
            </div>

            <button onClick={toggleFav} data-testid="favorite-button"
              className={`w-full mt-4 h-11 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors ${isFav ? "bg-emerald-600 text-white" : "border border-emerald-500 text-emerald-600"}`}>
              <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} /> {isFav ? t("product.inFav") : t("product.addFav")}
            </button>

            <ShareProduct product={product} />

            {/* Персональная рекомендация */}
            {evalRes.conflicts.length > 0 && (
              <div data-testid="rec-conflict" className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-3.5 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-700 text-sm">{t("rec.notRec")}</p>
                  <p className="text-sm text-red-600 mt-0.5 capitalize">{evalRes.conflicts.join(", ")}</p>
                </div>
              </div>
            )}
            {evalRes.conflicts.length === 0 && evalRes.matches.length > 0 && (
              <div data-testid="rec-suits" className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-3.5 flex gap-3 items-center">
                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shrink-0">✓</span>
                <p className="font-semibold text-emerald-700 text-sm">{t("rec.suits")}: {evalRes.matches.join(", ")}</p>
              </div>
            )}

            {/* NOVA + сертификаты */}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="badges-row">
              {nova && (
                <span className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold border"
                  style={{ background: nova >= 4 ? "#FEE2E2" : nova === 3 ? "#FEF3C7" : "#ECFDF5",
                           color: nova >= 4 ? "#B91C1C" : nova === 3 ? "#B45309" : "#047857",
                           borderColor: "transparent" }} data-testid="nova-badge">
                  NOVA {nova} · {t(`nova.n${nova}`)}
                </span>
              )}
              {certs.map((c) => (
                <span key={c} data-testid={`cert-${c}`} className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                  {t(`admin.cert.${c}`)}
                </span>
              ))}
            </div>

            {/* Negatives */}
            {facts.negatives.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-heading text-lg font-bold text-slate-900">{t("product.negatives")}</h2>
                  <span className="text-xs text-slate-400">{t("product.per100")}</span>
                </div>
                <div className="bg-[#FBF8F2] rounded-2xl border border-black/5 px-4 shadow-[0_2px_8px_rgba(60,50,35,0.06)]">
                  {facts.negatives.map((f) => (
                    <NutrientRow key={f.key} fact={f}
                      additives={f.key === "additives" ? additivesItems : null} />
                  ))}
                </div>
              </div>
            )}

            {/* Positives */}
            {facts.positives.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-heading text-lg font-bold text-slate-900">{t("product.positives")}</h2>
                  <span className="text-xs text-slate-400">{t("product.per100")}</span>
                </div>
                <div className="bg-[#FBF8F2] rounded-2xl border border-black/5 px-4 shadow-[0_2px_8px_rgba(60,50,35,0.06)]">
                  {facts.positives.map((f) => (
                    <NutrientRow key={f.key} fact={f}
                      additives={f.key === "additives" ? additivesItems : null} />
                  ))}
                </div>
              </div>
            )}

            {/* Composition */}
            {product.ingredients_text && (
              <div className="mt-6">
                <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">{t("product.composition")}</h2>
                <p className="text-sm text-slate-500 leading-relaxed bg-[#FBF8F2] border border-black/5 rounded-2xl p-4">{product.ingredients_text}</p>
              </div>
            )}
            {product.allergens?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {product.allergens.map((a, i) => (
                  <span key={i} className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">{a}</span>
                ))}
              </div>
            )}

            {/* Recommendations */}
            {alts.length === 0 && product.score.total < 75 && (
              <div className="mt-7" data-testid="no-alternatives">
                <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">{t("product.recommendations")}</h2>
                <p className="text-sm text-slate-500 bg-[#FBF8F2] border border-black/5 rounded-2xl p-4">{t("product.noAlternatives")}</p>
              </div>
            )}
            {alts.length > 0 && (
              <div className="mt-7">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-lg font-bold text-slate-900">{t("product.recommendations")}</h2>
                  <button onClick={() => navigate("/recs")} data-testid="see-all-recs" className="text-emerald-600 text-sm font-semibold flex items-center gap-1">
                    {t("common.seeAll")} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="alternatives-section">
                  {alts.map((a) => (
                    <div key={a.barcode} className="w-36 shrink-0"><GridCard item={a} badge="good" /></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
